import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, Download, X, ChevronDown, CheckCheck, Square, BookImage, Star } from 'lucide-react';
import { ExportConfig } from '../../types';
import styles from './BatchActionBar.module.css';

interface BatchActionBarProps {
  selectedCount: number;
  totalCount: number;
  exportConfigs: ExportConfig[];
  onDelete: () => void;
  onExportWithPreset: (config: ExportConfig) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  /** True while "Select all" is loading not-yet-fetched pages. */
  selectingAll?: boolean;
  onExit: () => void;
  deleting: boolean;
  /** Optional: if provided, an "Album" button appears (only shown to creators) */
  onAddToAlbum?: () => void;
  /** Optional: if provided, a "Highlight" button appears (only shown to creators) */
  onHighlight?: () => void;
  /** Optional: plain "Download" of the selected photos (client-side ZIP). */
  onDownload?: () => void;
  /** True while a download is being prepared. */
  downloading?: boolean;
}

export function BatchActionBar({
  selectedCount,
  totalCount,
  exportConfigs,
  onDelete,
  onExportWithPreset,
  onSelectAll,
  onDeselectAll,
  selectingAll = false,
  onExit,
  deleting,
  onAddToAlbum,
  onHighlight,
  onDownload,
  downloading = false,
}: BatchActionBarProps) {
  const [presetMenuOpen, setPresetMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setPresetMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // `>=` not `===`: "Select all" can select more ids than are currently loaded
  // (totalCount only counts loaded photos), so the toggle must still read as all.
  const allSelected = selectedCount > 0 && selectedCount >= totalCount;

  return (
    <motion.div
      className={styles.bar}
      initial={{ x: '-50%', y: 96, opacity: 0, scale: 0.96 }}
      animate={{ x: '-50%', y: 0, opacity: 1, scale: 1 }}
      exit={{ x: '-50%', y: 96, opacity: 0, scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
    >
      <div className={styles.inner}>

        {/* Select all / deselect all */}
        <motion.button
          onClick={allSelected ? onDeselectAll : onSelectAll}
          disabled={selectingAll}
          className={`${styles.btn} ${styles['btn-select']}`}
          whileTap={{ scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 500, damping: 25 }}
        >
          <AnimatePresence mode="wait" initial={false}>
            {allSelected ? (
              <motion.span
                key="check"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ duration: 0.12 }}
                className={styles['icon-flex-display']}
              >
                <CheckCheck className={styles['icon-sm-noshrink']} />
              </motion.span>
            ) : (
              <motion.span
                key="square"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ duration: 0.12 }}
                className={styles['icon-flex-display']}
              >
                <Square className={styles['icon-sm-noshrink']} />
              </motion.span>
            )}
          </AnimatePresence>
          <motion.span
            key={selectingAll ? 'loading' : selectedCount}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
          >
            {selectingAll ? 'Loading all…' : `${selectedCount} selected`}
          </motion.span>
        </motion.button>

        <div className={styles.divider} />

        {/* Delete */}
        <motion.button
          onClick={onDelete}
          disabled={selectedCount === 0 || deleting}
          className={`${styles.btn} ${styles['btn-delete']}`}
          whileTap={{ scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 500, damping: 25 }}
          title={deleting ? 'Deleting…' : 'Delete'}
        >
          <Trash2 className={styles['icon-sm']} />
          <span className={styles['text-desktop']}>{deleting ? 'Deleting…' : 'Delete'}</span>
        </motion.button>

        {/* Plain download of the selection — the originals, archived in the
            browser. Distinct from "Export with preset", which resizes and
            re-bakes overlays per platform. */}
        {onDownload && (
          <motion.button
            onClick={onDownload}
            disabled={selectedCount === 0 || downloading}
            className={`${styles.btn} ${styles['btn-export']}`}
            whileTap={{ scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            title={downloading ? 'Preparing…' : 'Download selected photos'}
          >
            <Download className={styles['icon-sm']} />
            <span className={styles['text-desktop']}>
              {downloading ? 'Preparing…' : 'Download'}
            </span>
          </motion.button>
        )}

        {/* Export with preset */}
        <div className={styles['export-wrapper']} ref={menuRef}>
          <motion.button
            onClick={() => setPresetMenuOpen((p) => !p)}
            disabled={selectedCount === 0}
            className={`${styles.btn} ${styles['btn-export']}`}
            whileTap={{ scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            title="Export with preset"
          >
            <Download className={styles['icon-sm']} />
            <span className={styles['text-desktop']}>Export with preset</span>
            <ChevronDown
              className={`${styles['icon-chevron']} ${styles.chevron} ${presetMenuOpen ? styles['chevron-open'] : ''}`}
            />
          </motion.button>

          <AnimatePresence>
            {presetMenuOpen && (
              <motion.div
                className={styles.dropdown}
                initial={{ opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.96 }}
                transition={{ type: 'spring', stiffness: 480, damping: 30 }}
              >
                <ul className={styles['dropdown-list']}>
                  <li>
                    <button
                      className={styles['dropdown-item']}
                      onClick={() => {
                        setPresetMenuOpen(false);
                        onExportWithPreset({
                          _id: 'originals',
                          eventId: 'none',
                          name: 'Original Photos',
                          filters: { brightness: 0, contrast: 0, saturation: 0, grayscale: false },
                          overlay: null,
                          selectedPlatforms: [],
                          selectedFormats: []
                        });
                      }}
                    >
                      <span className={styles['dropdown-item-name']}>Original Photos</span>
                      <span className={styles['dropdown-item-meta']}>Unedited originals</span>
                    </button>
                  </li>
                  {exportConfigs.length > 0 && (
                    <div style={{ height: 1, background: 'rgba(128,128,128,0.2)', margin: '4px 6px' }} />
                  )}
                  {exportConfigs.map((cfg) => (
                    <li key={cfg._id}>
                      <button
                        className={styles['dropdown-item']}
                        onClick={() => {
                          setPresetMenuOpen(false);
                          onExportWithPreset(cfg);
                        }}
                      >
                        <span className={styles['dropdown-item-name']}>{cfg.name}</span>
                        {cfg.selectedPlatforms.length > 0 && (
                          <span className={styles['dropdown-item-meta']}>
                            {cfg.selectedPlatforms.join(', ')}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Highlight (creator only) */}
        {onHighlight && (
          <motion.button
            onClick={onHighlight}
            disabled={selectedCount === 0}
            className={`${styles.btn} ${styles['btn-neutral']}`}
            whileTap={{ scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            title="Mark as highlight"
          >
            <Star className={styles['icon-sm']} />
            <span className={styles['text-desktop']}>Highlight</span>
          </motion.button>
        )}

        {/* Add to Album (creator only) */}
        {onAddToAlbum && (
          <>
            <motion.button
              onClick={onAddToAlbum}
              disabled={selectedCount === 0}
              className={`${styles.btn} ${styles['btn-neutral']}`}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
              title="Add to album"
            >
              <BookImage className={styles['icon-sm']} />
              <span className={styles['text-desktop']}>Album</span>
            </motion.button>
          </>
        )}

        <div className={styles.divider} />

        {/* Exit */}
        <motion.button
          onClick={onExit}
          className={`${styles.btn} ${styles['btn-exit']}`}
          whileTap={{ scale: 0.9 }}
          title="Exit selection"
        >
          <X className={styles['icon-exit']} />
        </motion.button>
      </div>
    </motion.div>
  );
}
